import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, FileCheck, CreditCard, TrendingUp, Trophy, Target,
  BarChart3, ArrowUp, ArrowDown, Minus, ChevronRight, Crown,
  Building2, MapPin, Calendar, Percent, Mail, UserCheck, X, Download
} from "lucide-react";
import { API } from "../config";

const StatCard = ({ label, value, icon: Icon, color = "text-lojack-red", bg = "bg-red-500/10", sub }) => (
  <div className="bg-[#111] border border-gray-800 rounded-xl p-4" data-testid={`perf-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
        <Icon size={20} className={color} />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-gray-500 text-xs">{label}</p>
        {sub && <p className="text-gray-600 text-[10px] mt-0.5">{sub}</p>}
      </div>
    </div>
  </div>
);

const TrendBar = ({ data, maxVal }) => {
  if (!data || data.length === 0) return null;
  const max = maxVal || Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-[3px] h-8">
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center gap-0.5" title={`${d.month}: ${d.count}`}>
          <div
            className="w-3 bg-lojack-red/70 rounded-sm transition-all hover:bg-lojack-red"
            style={{ height: `${Math.max((d.count / max) * 28, 2)}px` }}
          />
        </div>
      ))}
    </div>
  );
};

const DealerDetailPanel = ({ dealer, onClose }) => {
  if (!dealer) return null;
  const trend = dealer.monthly_trend || [];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0D0D0D] rounded-2xl w-full max-w-2xl max-h-[85vh] border border-gray-700 overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${dealer.is_active ? 'bg-lojack-red/20 text-lojack-red' : 'bg-gray-700 text-gray-500'}`}>
              {dealer.rank <= 3 ? <Crown size={22} /> : `#${dealer.rank}`}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{dealer.first_name} {dealer.last_name}</h2>
              <p className="text-gray-500 text-sm">{dealer.company_name} - {dealer.city} | <span className="font-mono text-gray-400">{dealer.dealer_code}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${API}/dashboard/dealers/${dealer.id}/report-pdf`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-lojack-red/10 text-lojack-red rounded-lg text-sm font-medium hover:bg-lojack-red/20 transition-colors">
              <Download size={16} /> PDF
            </a>
            <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-5 flex-1">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Clienti" value={dealer.total_customers} icon={Users} />
            <StatCard label="Contratti" value={dealer.total_contracts} icon={FileCheck} color="text-lojack-blue-400" bg="bg-lojack-blue-500/10" />
            <StatCard label="Card" value={dealer.total_cards} icon={CreditCard} color="text-blue-400" bg="bg-blue-500/10" />
            <StatCard label="Conversione" value={`${dealer.conversion_rate}%`} icon={Percent} color="text-lojack-clause-400" bg="bg-lojack-clause-500/10" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Questo Mese" value={dealer.this_month} icon={TrendingUp} color="text-cyan-400" bg="bg-cyan-500/10" />
            <StatCard label="Ultimi 7gg" value={dealer.last_7_days} icon={TrendingUp} color="text-orange-400" bg="bg-orange-500/10" />
            <StatCard label="Ultimi 30gg" value={dealer.last_30_days} icon={TrendingUp} color="text-purple-400" bg="bg-purple-500/10" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Venditori</p>
              <p className="text-2xl font-bold text-white">{dealer.sellers_count}</p>
            </div>
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Email Inviate</p>
              <p className="text-2xl font-bold text-white">{dealer.emails_sent}</p>
            </div>
          </div>

          {trend.length > 0 && (
            <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-3">Trend Ultimi 6 Mesi</p>
              <div className="flex items-end justify-between gap-2">
                {trend.map((t, i) => {
                  const max = Math.max(...trend.map(x => x.count), 1);
                  return (
                    <div key={i} className="flex flex-col items-center gap-1 flex-1">
                      <span className="text-white text-xs font-bold">{t.count}</span>
                      <div
                        className="w-full bg-lojack-red/60 rounded-t-md hover:bg-lojack-red transition-colors"
                        style={{ height: `${Math.max((t.count / max) * 80, 4)}px` }}
                      />
                      <span className="text-gray-600 text-[10px]">{t.month}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DealerPerformancePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [sortBy, setSortBy] = useState("total_customers");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/dashboard/dealers-performance`);
        setData(res.data);
      } catch {
        toast.error("Errore caricamento performance dealer");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <Minus size={12} className="text-gray-700" />;
    return sortDir === "desc" ? <ArrowDown size={12} className="text-lojack-red" /> : <ArrowUp size={12} className="text-lojack-red" />;
  };

  const sortedDealers = data?.dealers
    ? [...data.dealers].sort((a, b) => {
        const valA = a[sortBy] ?? 0;
        const valB = b[sortBy] ?? 0;
        return sortDir === "desc" ? valB - valA : valA - valB;
      })
    : [];

  const maxTrend = data?.dealers
    ? Math.max(...data.dealers.flatMap(d => (d.monthly_trend || []).map(t => t.count)), 1)
    : 1;

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      </div>
    );
  }

  if (!data) return null;
  const { summary, top_this_month, month } = data;

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in" data-testid="dealer-performance-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white" data-testid="perf-page-title">Performance Dealer</h1>
        <p className="text-gray-400 text-sm mt-1">Panoramica comparativa - {month}</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        <StatCard label="Dealer Totali" value={summary.total_dealers} icon={Building2} />
        <StatCard label="Dealer Attivi" value={summary.active_dealers} icon={UserCheck} color="text-lojack-blue-400" bg="bg-lojack-blue-500/10" />
        <StatCard label="Clienti Totali" value={summary.total_customers} icon={Users} color="text-blue-400" bg="bg-blue-500/10" />
        <StatCard label="Contratti" value={summary.total_contracts} icon={FileCheck} color="text-lojack-blue-400" bg="bg-lojack-blue-500/10" />
        <StatCard label="Card" value={summary.total_cards} icon={CreditCard} color="text-purple-400" bg="bg-purple-500/10" />
        <StatCard label="Media/Dealer" value={summary.avg_customers_per_dealer} icon={BarChart3} color="text-orange-400" bg="bg-orange-500/10" />
        <StatCard label="Conversione" value={`${summary.overall_conversion_rate}%`} icon={Percent} color="text-lojack-clause-400" bg="bg-lojack-clause-500/10" />
      </div>

      {/* Top Performer Banner */}
      {top_this_month && top_this_month.this_month > 0 && (
        <div className="mb-8 bg-gradient-to-r from-lojack-red/10 to-transparent border border-lojack-red/20 rounded-2xl p-5 flex items-center gap-4 cursor-pointer hover:border-lojack-red/40 transition-colors"
          onClick={() => setSelectedDealer(top_this_month)}
          data-testid="top-performer-banner">
          <div className="w-14 h-14 rounded-xl bg-lojack-red/20 flex items-center justify-center">
            <Crown size={28} className="text-lojack-red" />
          </div>
          <div className="flex-1">
            <p className="text-gray-400 text-xs uppercase tracking-wider">Top Performer del Mese</p>
            <p className="text-white text-xl font-bold">{top_this_month.first_name} {top_this_month.last_name}</p>
            <p className="text-gray-500 text-sm">{top_this_month.company_name} - {top_this_month.city}</p>
          </div>
          <div className="text-right">
            <p className="text-lojack-red text-3xl font-bold">{top_this_month.this_month}</p>
            <p className="text-gray-500 text-xs">clienti questo mese</p>
          </div>
          <ChevronRight size={20} className="text-gray-600" />
        </div>
      )}

      {/* Dealers Table */}
      <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden" data-testid="dealers-performance-table">
        {/* Table Header */}
        <div className="hidden lg:grid grid-cols-12 gap-2 px-5 py-3 border-b border-gray-800 bg-[#0D0D0D]">
          <div className="col-span-1 text-gray-500 text-xs font-medium uppercase">#</div>
          <div className="col-span-3 text-gray-500 text-xs font-medium uppercase">Dealer</div>
          <button onClick={() => handleSort("total_customers")} className="col-span-1 text-gray-500 text-xs font-medium uppercase flex items-center gap-1 hover:text-white">
            Clienti <SortIcon field="total_customers" />
          </button>
          <button onClick={() => handleSort("total_contracts")} className="col-span-1 text-gray-500 text-xs font-medium uppercase flex items-center gap-1 hover:text-white">
            Contratti <SortIcon field="total_contracts" />
          </button>
          <button onClick={() => handleSort("conversion_rate")} className="col-span-1 text-gray-500 text-xs font-medium uppercase flex items-center gap-1 hover:text-white">
            Conv. % <SortIcon field="conversion_rate" />
          </button>
          <button onClick={() => handleSort("this_month")} className="col-span-1 text-gray-500 text-xs font-medium uppercase flex items-center gap-1 hover:text-white">
            Mese <SortIcon field="this_month" />
          </button>
          <button onClick={() => handleSort("last_30_days")} className="col-span-1 text-gray-500 text-xs font-medium uppercase flex items-center gap-1 hover:text-white">
            30gg <SortIcon field="last_30_days" />
          </button>
          <div className="col-span-2 text-gray-500 text-xs font-medium uppercase">Trend 6M</div>
          <div className="col-span-1"></div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-gray-800/50">
          {sortedDealers.map((d, idx) => {
            const rankColors = {
              1: 'text-lojack-clause-400 bg-lojack-clause-500/10 border-lojack-clause-500/30',
              2: 'text-gray-300 bg-gray-400/10 border-gray-400/30',
              3: 'text-lojack-clause-600 bg-lojack-clause-700/10 border-lojack-clause-700/30',
            };
            const rankClass = rankColors[idx + 1] || 'text-gray-500 bg-gray-800/50 border-gray-700';

            return (
              <div
                key={d.id}
                className="lg:grid lg:grid-cols-12 lg:gap-2 px-5 py-4 hover:bg-white/[0.02] transition-colors cursor-pointer items-center"
                onClick={() => setSelectedDealer(d)}
                data-testid={`perf-dealer-row-${d.id}`}
              >
                {/* Rank */}
                <div className="col-span-1 mb-2 lg:mb-0">
                  <div className={`w-8 h-8 rounded-lg border flex items-center justify-center text-xs font-bold ${rankClass}`}>
                    {idx + 1}
                  </div>
                </div>

                {/* Dealer Info */}
                <div className="col-span-3 flex items-center gap-3 mb-2 lg:mb-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${d.is_active ? 'bg-lojack-red/15 text-lojack-red' : 'bg-gray-800 text-gray-600'}`}>
                    {d.first_name[0]}{d.last_name[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{d.first_name} {d.last_name}</p>
                    <p className="text-gray-600 text-xs truncate">{d.company_name || d.email}</p>
                  </div>
                </div>

                {/* Clienti */}
                <div className="col-span-1">
                  <span className="text-white font-bold">{d.total_customers}</span>
                  <span className="lg:hidden text-gray-600 text-xs ml-1">clienti</span>
                </div>

                {/* Contratti */}
                <div className="col-span-1">
                  <span className="text-lojack-blue-400 font-bold">{d.total_contracts}</span>
                  <span className="lg:hidden text-gray-600 text-xs ml-1">contratti</span>
                </div>

                {/* Conversione */}
                <div className="col-span-1">
                  <span className={`font-bold text-sm ${d.conversion_rate >= 70 ? 'text-lojack-blue-400' : d.conversion_rate >= 40 ? 'text-lojack-clause-400' : 'text-gray-400'}`}>
                    {d.conversion_rate}%
                  </span>
                </div>

                {/* Questo Mese */}
                <div className="col-span-1">
                  <span className="text-cyan-400 font-bold">{d.this_month}</span>
                </div>

                {/* Ultimi 30gg */}
                <div className="col-span-1">
                  <span className="text-orange-400 font-bold">{d.last_30_days}</span>
                </div>

                {/* Trend */}
                <div className="col-span-2 hidden lg:block">
                  <TrendBar data={d.monthly_trend} maxVal={maxTrend} />
                </div>

                {/* Arrow */}
                <div className="col-span-1 hidden lg:flex justify-end">
                  <ChevronRight size={16} className="text-gray-700" />
                </div>
              </div>
            );
          })}
        </div>

        {sortedDealers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users size={40} className="mx-auto mb-3 text-gray-700" />
            <p>Nessun dealer registrato</p>
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selectedDealer && (
        <DealerDetailPanel dealer={selectedDealer} onClose={() => setSelectedDealer(null)} />
      )}
    </div>
  );
};

export default DealerPerformancePage;
